import type { Stadt } from './stadt';

// Bedarf Status enum matching backend
export enum BedarfStatus {
  NOCH_NICHT_RELEVANT = 'NOCH_NICHT_RELEVANT',
  ANFRAGE = 'ANFRAGE',
  OFFEN = 'OFFEN',
  KENNENLERNGESPRAECH = 'KENNENLERNGESPRAECH',
  TERMINIERT = 'TERMINIERT',
  KOSTENTRAEGER_GO = 'KOSTENTRAEGER_GO',
  ONBOARDING = 'ONBOARDING',
  GEWONNEN = 'GEWONNEN',
  VERLOREN = 'VERLOREN',
}

// Bedarf Priority enum matching backend
export enum BedarfPrioritaet {
  NIEDRIG = 'NIEDRIG',
  MITTEL = 'MITTEL',
  HOCH = 'HOCH',
}

// Gender enum matching backend
export enum Gender {
  MAENNLICH = 'MAENNLICH',
  WEIBLICH = 'WEIBLICH',
  DIVERS = 'DIVERS',
}

// User reference (for createdBy)
export interface UserRef {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Bewerber reference (for kennenlernen/zuordnungen)
export interface BewerberRef {
  id: string;
  firstName: string;
  lastName: string;
}

// Kennenlernen interface (basic)
export interface KennenlernenRef {
  id: string;
  scheduledAt: string;
  status: string;
  outcome: string;
  bewerber?: BewerberRef;
}

// Zuordnung interface (basic)
export interface ZuordnungRef {
  id: string;
  startDate: string;
  endDate?: string;
  status: string;
  bewerber?: BewerberRef;
}

/**
 * The Schulbegleiter working a position. Distinct from `assignedTo`, which is the
 * internal caseworker, and from a Bewerber's `beworbenAufBedarf`, which is only
 * what they applied for.
 */
export interface ZugeordneterBewerber {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  status: string;
}

/**
 * One row of the Schulbegleiter picker (GET /bedarfe/:id/zuordnung/kandidaten).
 * Someone assigned elsewhere comes back with that Bedarf named, so the picker
 * can show them disabled instead of offering a choice that would end in a 409.
 */
export interface ZuordnungKandidat {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: string;
  cityName: string | null;
  zugeordnetHier: boolean;
  zugeordnetAnderswo: { id: string; name: string } | null;
}

// Bedarf interface (full)
export interface Bedarf {
  id: string;

  // Stammdaten
  childFirstName: string;
  childLastName: string | null;
  childDateOfBirth: string | null;
  childGender: Gender | null;
  childStreet: string | null;
  childCityId: string | null;
  childCity: Stadt | null;
  childPostalCode: string | null;

  // Schulische Angaben
  schoolName: string | null;
  schoolStreet: string | null;
  schoolCityId: string | null;
  schoolCity: Stadt | null;
  schoolPostalCode: string | null;
  schoolClass: string | null;

  // Bedarf & Diagnose
  diagnosis: string | null;
  supportDescription: string | null;
  specialNeeds: string | null;

  // Stundenplan - Start/End times per day (HH:MM format)
  startTimeMonday: string | null;
  endTimeMonday: string | null;
  startTimeTuesday: string | null;
  endTimeTuesday: string | null;
  startTimeWednesday: string | null;
  endTimeWednesday: string | null;
  startTimeThursday: string | null;
  endTimeThursday: string | null;
  startTimeFriday: string | null;
  endTimeFriday: string | null;
  includesBreaksOgs: boolean;

  // Rahmenbedingungen
  startDateRequested: string | null;
  endDateRequested: string | null;
  currentSituation: string | null;

  // Kostenträger / Formalien
  kostentraegerName: string | null;
  kostentraegerAnsprechpartner: string | null;
  kostentraegerEmail: string | null;
  kostentraegerPhone: string | null;
  hasApproval: boolean;
  hasCarePlan: boolean;
  legalBasis: string | null;

  // Elternkontakt
  parentName: string | null;
  parentPhone: string | null;
  parentEmail: string | null;

  // Onboarding-Schritte
  onboardingKostentraegerKilanka: boolean;
  onboardingSchuleKilanka: boolean;
  onboardingKlientKilanka: boolean;
  onboardingStartSchuleAbgesprochen: boolean;
  onboardingStartKostentraegerAbgesprochen: boolean;
  onboardingMitarbeiterAbgeschlossen: boolean;
  onboardingBewilligungAbgelegt: boolean;
  onboardingSchweigepflichtAbgelegt: boolean;
  onboardingPersonalstammdatenGeprueft: boolean;

  // Meta
  status: BedarfStatus;
  /** Wann der Bedarf in seinen aktuellen Status kam. Ordnet die Kanban-Spalten. */
  statusChangedAt: string;
  priority: BedarfPrioritaet;
  notes: string | null;
  assignedToId: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;

  // Zuordnung (der eingesetzte Schulbegleiter)
  zugeordneterBewerberId: string | null;
  zugeordnetAt: string | null;

  // Relations
  createdBy?: UserRef;
  assignedTo?: UserRef | null;
  zugeordneterBewerber?: ZugeordneterBewerber | null;
  kennenlernen?: KennenlernenRef[];
  zuordnungen?: ZuordnungRef[];
  _count?: {
    kennenlernen: number;
    zuordnungen: number;
  };
}

// Create Bedarf DTO
export interface CreateBedarfDto {
  // Stammdaten (required: only childFirstName)
  childFirstName: string;
  childLastName?: string;
  childDateOfBirth?: string;
  childGender?: Gender;
  childStreet?: string;
  childCityId?: string;
  childPostalCode?: string;

  // Schulische Angaben (schoolCityId required)
  schoolName?: string;
  schoolStreet?: string;
  schoolCityId: string;
  schoolPostalCode?: string;
  schoolClass?: string;

  // Bedarf & Diagnose
  diagnosis?: string;
  supportDescription?: string;
  specialNeeds?: string;

  // Stundenplan - Start/End times per day (HH:MM format)
  startTimeMonday?: string;
  endTimeMonday?: string;
  startTimeTuesday?: string;
  endTimeTuesday?: string;
  startTimeWednesday?: string;
  endTimeWednesday?: string;
  startTimeThursday?: string;
  endTimeThursday?: string;
  startTimeFriday?: string;
  endTimeFriday?: string;
  includesBreaksOgs?: boolean;

  // Rahmenbedingungen
  startDateRequested?: string;
  endDateRequested?: string;
  currentSituation?: string;

  // Kostenträger / Formalien
  kostentraegerName?: string;
  kostentraegerAnsprechpartner?: string;
  kostentraegerEmail?: string;
  kostentraegerPhone?: string;
  hasApproval?: boolean;
  hasCarePlan?: boolean;
  legalBasis?: string;

  // Elternkontakt
  parentName?: string;
  parentPhone?: string;
  parentEmail?: string;

  // Meta
  status?: BedarfStatus;
  priority?: BedarfPrioritaet;
  notes?: string;
}

// Update Bedarf DTO
export interface UpdateBedarfDto {
  // Stammdaten
  childFirstName?: string;
  childLastName?: string;
  childDateOfBirth?: string;
  childGender?: Gender;
  childStreet?: string;
  childCityId?: string;
  childPostalCode?: string;

  // Schulische Angaben
  schoolName?: string;
  schoolStreet?: string;
  schoolCityId?: string;
  schoolPostalCode?: string;
  schoolClass?: string;

  // Bedarf & Diagnose
  diagnosis?: string;
  supportDescription?: string;
  specialNeeds?: string;

  // Stundenplan - Start/End times per day (HH:MM format)
  startTimeMonday?: string;
  endTimeMonday?: string;
  startTimeTuesday?: string;
  endTimeTuesday?: string;
  startTimeWednesday?: string;
  endTimeWednesday?: string;
  startTimeThursday?: string;
  endTimeThursday?: string;
  startTimeFriday?: string;
  endTimeFriday?: string;
  includesBreaksOgs?: boolean;

  // Rahmenbedingungen
  startDateRequested?: string;
  endDateRequested?: string;
  currentSituation?: string;

  // Kostenträger / Formalien
  kostentraegerName?: string;
  kostentraegerAnsprechpartner?: string;
  kostentraegerEmail?: string;
  kostentraegerPhone?: string;
  hasApproval?: boolean;
  hasCarePlan?: boolean;
  legalBasis?: string;

  // Elternkontakt
  parentName?: string;
  parentPhone?: string;
  parentEmail?: string;

  // Onboarding-Schritte
  onboardingKostentraegerKilanka?: boolean;
  onboardingSchuleKilanka?: boolean;
  onboardingKlientKilanka?: boolean;
  onboardingStartSchuleAbgesprochen?: boolean;
  onboardingStartKostentraegerAbgesprochen?: boolean;
  onboardingMitarbeiterAbgeschlossen?: boolean;
  onboardingBewilligungAbgelegt?: boolean;
  onboardingSchweigepflichtAbgelegt?: boolean;
  onboardingPersonalstammdatenGeprueft?: boolean;

  // Meta
  status?: BedarfStatus;
  priority?: BedarfPrioritaet;
  notes?: string;
  assignedToId?: string | null;
}

// Query parameters for listing
export interface QueryBedarfDto {
  search?: string;
  status?: BedarfStatus;
  priority?: BedarfPrioritaet;
  city?: string;
  schoolCityId?: string;
  assignedUserId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'updatedAt' | 'createdAt' | 'statusChangedAt' | 'childLastName' | 'status' | 'priority' | 'startDateRequested';
  sortOrder?: 'asc' | 'desc';
}

// Bedarf statistics
export interface BedarfStatistics {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  newThisMonth: number;
}

// Dashboard: recent Bedarf lifecycle events (CREATE / STATUS_CHANGE / DELETE)
export type BedarfRecentEventType =
  | 'BEDARF_CREATE'
  | 'BEDARF_STATUS_CHANGE'
  | 'BEDARF_DELETE';

export interface BedarfRecentEvent {
  id: string;
  bedarfId: string;
  eventType: BedarfRecentEventType;
  occurredAt: string;
  user: { id: string; firstName: string; lastName: string } | null;
  bedarf: {
    childFirstName: string;
    childLastName: string | null;
    schoolCityName: string | null;
    currentStatus: BedarfStatus | null;
  };
  statusChange?: { from: BedarfStatus; to: BedarfStatus };
}

// Dashboard: Bedarf counts grouped by school city
export interface SchoolCityCounts {
  schoolCityId: string | null;
  schoolCityName: string;
  offen: number;
  kennenlernen: number;
  anfrage: number;
  highlighted: number;
}

// Status display configuration.
// `description` explains what the status means and when a Bedarf belongs there —
// shown as an info tooltip on the Kanban column headers.
export const BedarfStatusConfig: Record<
  BedarfStatus,
  { label: string; color: string; bgColor: string; description: string }
> = {
  [BedarfStatus.NOCH_NICHT_RELEVANT]: {
    label: 'Noch nicht relevant',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    description:
      'Bedarf ist erfasst, steht aber noch nicht im Bewilligungsprozess beim Amt. In der Regel haben die Eltern noch keinen Kontakt zum Amt aufgenommen.',
  },
  [BedarfStatus.ANFRAGE]: {
    label: 'Anfragen',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    description:
      'Bedarf ist in Anbahnung, aber noch nicht bewilligt — deshalb kann er auch noch nicht besetzt werden.',
  },
  [BedarfStatus.OFFEN]: {
    label: 'Offen',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    description: 'Bedarf ist bewilligt. Wir können theoretisch sofort starten und besetzen.',
  },
  [BedarfStatus.KENNENLERNGESPRAECH]: {
    label: 'Kennenlerngespräch',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    description:
      'Eine mögliche Schulbegleitung ist gefunden. Wir stimmen gerade einen Termin für das Kennenlerngespräch ab.',
  },
  [BedarfStatus.TERMINIERT]: {
    label: 'Terminiert',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    description: 'Das Kennenlerngespräch ist mit allen Beteiligten abgestimmt und steht.',
  },
  [BedarfStatus.KOSTENTRAEGER_GO]: {
    label: 'Kostenträger Go',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-100',
    description:
      'Kennenlerngespräch war erfolgreich. Eltern und Bewerber wollen zu vereinbarten Konditionen starten. Die Bestätigung durch den Kostenträger muss noch eingeholt werden.',
  },
  [BedarfStatus.ONBOARDING]: {
    label: 'Onboarding',
    color: 'text-teal-700',
    bgColor: 'bg-teal-100',
    description:
      'Der Kostenträger hat zugestimmt — alle Beteiligten sind sich einig. Jetzt läuft die Onboarding-Checkliste.',
  },
  [BedarfStatus.GEWONNEN]: {
    label: 'Gewonnen',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    description:
      'Das Onboarding ist vollständig abgeschlossen. Bis zum Start der Begleitung ist nichts weiter zu tun — der Bedarf bleibt hier, solange die Begleitung läuft.',
  },
  [BedarfStatus.VERLOREN]: {
    label: 'Verloren',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    description:
      'Für diesen Bedarf wird keine Schulbegleitung gestellt — er wird auch nicht mehr benötigt.',
  },
};

// Priority display configuration
export const BedarfPrioritaetConfig: Record<BedarfPrioritaet, { label: string; color: string; bgColor: string }> = {
  [BedarfPrioritaet.NIEDRIG]: { label: 'Niedrig', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  [BedarfPrioritaet.MITTEL]: { label: 'Mittel', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  [BedarfPrioritaet.HOCH]: { label: 'Hoch', color: 'text-orange-600', bgColor: 'bg-orange-100' },
};

// Gender display configuration
export const GenderConfig: Record<Gender, { label: string }> = {
  [Gender.MAENNLICH]: { label: 'Männlich' },
  [Gender.WEIBLICH]: { label: 'Weiblich' },
  [Gender.DIVERS]: { label: 'Divers' },
};

// Status order for Kanban
export const BEDARF_STATUS_ORDER: BedarfStatus[] = [
  BedarfStatus.NOCH_NICHT_RELEVANT,
  BedarfStatus.ANFRAGE,
  BedarfStatus.OFFEN,
  BedarfStatus.KENNENLERNGESPRAECH,
  BedarfStatus.TERMINIERT,
  BedarfStatus.KOSTENTRAEGER_GO,
  BedarfStatus.ONBOARDING,
  BedarfStatus.GEWONNEN,
  BedarfStatus.VERLOREN,
];

// Onboarding checklist steps (single source of truth for checkboxes,
// info tooltips and the client-side "all done" guard before GEWONNEN).
export interface OnboardingStep {
  field: keyof Pick<
    Bedarf,
    | 'onboardingKostentraegerKilanka'
    | 'onboardingSchuleKilanka'
    | 'onboardingKlientKilanka'
    | 'onboardingStartSchuleAbgesprochen'
    | 'onboardingStartKostentraegerAbgesprochen'
    | 'onboardingMitarbeiterAbgeschlossen'
    | 'onboardingBewilligungAbgelegt'
    | 'onboardingSchweigepflichtAbgelegt'
    | 'onboardingPersonalstammdatenGeprueft'
  >;
  label: string;
  description: string;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    field: 'onboardingPersonalstammdatenGeprueft',
    label: 'Personalstammdaten geprüft',
    description: 'Von Sorgeberechtigten und Kind',
  },
  {
    field: 'onboardingKostentraegerKilanka',
    label: 'Kostenträger in Kilanka gepflegt',
    description: 'Stammdaten, Preis, Ansprechpartner und Rechnungsempfänger',
  },
  {
    field: 'onboardingSchuleKilanka',
    label: 'Schule in Kilanka gepflegt',
    description: 'Stammdaten',
  },
  {
    field: 'onboardingKlientKilanka',
    label: 'Klient in Kilanka gepflegt',
    description:
      'Stammdaten, Schule, Maßnahme mit Bewilligungszeitraum, Mitarbeiter, Zuständiger Mitarbeiter & Aktenzeichen des Kostenträgers',
  },
  {
    field: 'onboardingStartSchuleAbgesprochen',
    label: 'Start mit Schule abgesprochen',
    description: 'Info Mail gesendet, Startdatum und Zeit abgesprochen & Onboarding in Schule geklärt',
  },
  {
    field: 'onboardingStartKostentraegerAbgesprochen',
    label: 'Start mit Kostenträger abgesprochen',
    description:
      'Uns wurde bestätigt, dass wir mit dem Mitarbeiter das Kind an der geplanten Schule ab Startdatum begleiten dürfen. — Bewilligung u. ggf. AZ anfordern',
  },
  {
    field: 'onboardingMitarbeiterAbgeschlossen',
    label: 'Mitarbeiter Onboarding abgeschlossen',
    description: 'Der Bewerber steht im CRM auf gewonnen',
  },
  {
    field: 'onboardingBewilligungAbgelegt',
    label: 'Bewilligung abgelegt',
    description: 'Das Dokument ist auf der Synologie im Klientenordner abgelegt',
  },
  {
    field: 'onboardingSchweigepflichtAbgelegt',
    label: 'Schweigepflichtsentbindung abgelegt',
    description:
      'Die Schweigepflichtsentbindung ist von allen Sorgeberechtigten unterschrieben und auf der Synologie im Klientenordner abgelegt',
  },
];

// How many onboarding steps on the given Bedarf are still unchecked. Open steps
// no longer block GEWONNEN — they only make the move ask first.
export const offeneOnboardingSchritte = (bedarf: Bedarf): number =>
  ONBOARDING_STEPS.filter((step) => !bedarf[step.field]).length;

// Helper to format child name
export const formatChildName = (bedarf: Bedarf): string => {
  if (bedarf.childLastName) {
    return `${bedarf.childFirstName} ${bedarf.childLastName}`;
  }
  return bedarf.childFirstName;
};

/**
 * Calculate hours from a time range (HH:MM format)
 */
export const calculateHoursFromTimeRange = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): number => {
  if (!startTime || !endTime) return 0;
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const minutes = endH * 60 + endM - startH * 60 - startM;
  return Math.max(0, minutes / 60);
};

/** Ab dieser Anwesenheit am Tag gilt die Pflichtpause (§ 4 ArbZG: mehr als sechs Stunden). */
export const BREAK_THRESHOLD_HOURS = 6;

/** Die Pflichtpause, die an einem solchen Tag keine Arbeitszeit ist. */
export const STATUTORY_BREAK_HOURS = 0.5;

/**
 * Arbeitszeit eines Tages aus seiner Anwesenheit: über sechs Stunden fallen
 * 30 Minuten Pause weg — unabhängig vom Haken „inkl. Pausen/OGS", der nur ein
 * Hinweis ist. Spiegelt `deductStatutoryBreak` im Backend (schedule.utils.ts).
 */
export const deductStatutoryBreak = (hours: number): number =>
  hours > BREAK_THRESHOLD_HOURS ? hours - STATUTORY_BREAK_HOURS : hours;

/** Arbeitsstunden zwischen zwei Zeiten, nach Abzug der Pflichtpause. */
export const calculateWorkingHoursFromTimeRange = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): number => deductStatutoryBreak(calculateHoursFromTimeRange(startTime, endTime));

/**
 * Calculate daily working hours (statutory break deducted) for each day
 */
export const calculateDailyHours = (bedarf: Partial<Bedarf>): {
  hoursMonday: number;
  hoursTuesday: number;
  hoursWednesday: number;
  hoursThursday: number;
  hoursFriday: number;
} => {
  return {
    hoursMonday: calculateWorkingHoursFromTimeRange(bedarf.startTimeMonday, bedarf.endTimeMonday),
    hoursTuesday: calculateWorkingHoursFromTimeRange(bedarf.startTimeTuesday, bedarf.endTimeTuesday),
    hoursWednesday: calculateWorkingHoursFromTimeRange(bedarf.startTimeWednesday, bedarf.endTimeWednesday),
    hoursThursday: calculateWorkingHoursFromTimeRange(bedarf.startTimeThursday, bedarf.endTimeThursday),
    hoursFriday: calculateWorkingHoursFromTimeRange(bedarf.startTimeFriday, bedarf.endTimeFriday),
  };
};

/**
 * Calculate total weekly working hours from time ranges (breaks deducted per day)
 */
export const calculateTotalHours = (bedarf: Partial<Bedarf>): number => {
  const daily = calculateDailyHours(bedarf);
  return daily.hoursMonday + daily.hoursTuesday + daily.hoursWednesday + daily.hoursThursday + daily.hoursFriday;
};

/**
 * Format hours as "Xh Ymin" for display
 */
export const formatHours = (hours: number): string => {
  if (hours === 0) return '0h';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}min`;
  return `${h}h ${m}min`;
};

/**
 * Validate that end time is after start time
 */
export const isValidTimeRange = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): boolean => {
  if (!startTime || !endTime) return true; // Empty is valid
  return endTime > startTime;
};
